import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Form from "components/forms/form"
import FormErrors from "components/formParts/formErrors"
import TextInput from "components/inputs/textInput"
import Button from "components/inputs/button"
import ButtonGroup from "components/buttons/buttonGroup"
import ProcessingButton from "components/inputs/processingButton"
import Section from "components/layout/section"
import { ReactComponent as DeleteIcon } from "assets/icons/delete.svg"
import useAuth from "hooks/useAuth"
import useLayout from "hooks/useLayout"
import { decodeFirebaseError } from "utils/firebaseHelpers"
import { logger } from "utils/secureLogger"

const DeleteAccountForm = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { addToast } = useLayout()
    const { user, deleteAccount, isDeletingAccount } = useAuth()

    const [confirmation, setConfirmation] = useState("")
    const [errors, setErrors] = useState<string[]>([])

    const email = user?.email || ""
    const confirmed = confirmation.trim().toLowerCase() === email.trim().toLowerCase() && email.length > 0

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!confirmed) return
        setErrors([])

        try {
            await deleteAccount({ email })
            addToast({ text: t("toastAccountDeleted"), colorScheme: "green" })
            navigate("/")
        } catch (error: unknown) {
            logger.error("Failed to delete account", error)
            const errorMessage = decodeFirebaseError(error instanceof Error ? error.message : String(error))
            const translated = t(errorMessage, { defaultValue: errorMessage })
            setErrors([translated])
            addToast({ text: t("toastAccountDeleteError"), colorScheme: "red" })
        }
    }

    return (
        <Form onSubmit={handleSubmit} className="group" isLoading={isDeletingAccount}>
            <Section>
                <p>{t("deleteAccountConfirmation")}</p>
                <p>{t("deleteAccountOwnedStoragesNote")}</p>
                <TextInput
                    label={t("typeEmailToConfirm")}
                    placeholder={email}
                    value={confirmation}
                    onChange={(e) => setConfirmation(e.target.value)}
                    autoComplete="email"
                    required
                />
                <ButtonGroup>
                    {isDeletingAccount ? (
                        <ProcessingButton design="danger" />
                    ) : (
                        <Button wideOnMobile type="submit" design="danger" disabled={!confirmed}>
                            <DeleteIcon />
                            <span>{t("buttonDeleteAccount")}</span>
                        </Button>
                    )}
                </ButtonGroup>
                {errors.length > 0 && <FormErrors errors={errors} />}
            </Section>
        </Form>
    )
}

export default DeleteAccountForm
